import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import moment from "moment";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CalendarIcon from "../Components/CalendarIcon";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import BoardsContext from "./boardsContext";
import useTimeline from "../UserTimeline/useTimeline";

function TimeViewSelector({ selected, handleSelected }) {
  const { moodboards, moodboardDispatch } = useContext(BoardsContext);
  const { t } = useTranslation();
  const options = [
    {
      id: "lastweeks",
      title: t("moodboards.buttons.lastweek"),
      icon: <CalendarIcon number={7} />,
    },
    {
      id: "last2weeks",
      title: t("moodboards.buttons.last2weeks"),
      icon: <CalendarIcon number={14} />,
    },
    {
      id: "last4weeks",
      title: t("moodboards.buttons.last4weeks"),
      icon: <CalendarIcon number={32} />,
    },
    {
      id: "allTime",
      title: t("moodboards.buttons.allTime"),
      icon: <CalendarMonthIcon/>,
    },
  ];

  const { timeline } = useTimeline();
  const timePoints =
    timeline?.events
      ?.sort((event1, event2) => event1.time - event2.time)
      .map((event) => event.time) ?? []; // array of timeStamps, extracted from timeline

  const handleView = (view) => {
    handleSelected(view);
    const newTimeRange = moodboards.map((moodboard) => {
      let min = timePoints[0];
      let max = timePoints[timePoints.length - 1];
      if (view === "lastweeks") {
        min = moment(max).subtract(1, "weeks").valueOf();
      }
      if (view === "last2weeks") {
        min = moment(max).subtract(2, "weeks").valueOf();
      }
      if (view === "last4weeks") {
        min = moment(max).subtract(4, "weeks").valueOf();
      }
      return { ...moodboard, ...{ timeRange: [min, max], view, update: true } };
    });
    moodboardDispatch({ type: "SET_MOODBOARDS", payload: newTimeRange });
  };

  const [openMenu, setOpenMenu] = React.useState(false);
  const openMenuViewFilter = Boolean(openMenu);
  const handleViewFilterMenu = (event) => {
    setOpenMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setOpenMenu(false);
  };

  const handleMenuItemClick = (option) => {
    handleView(option.id);
    handleMenuClose();
  };

  return (
    <div>
      <Button
        aria-label="choose a view filter"
        aria-controls="menu-timeline-filter-view"
        aria-haspopup="true"
        onClick={handleViewFilterMenu}
        size="small"
        endIcon={<ArrowDropDownIcon />}
        color="inherit"
      >
        <Typography variant="subtitle2" color="textSecondary">
          {options.find((option) => option.id === selected)?.title ??
            t("moodboards.settings.range")}
        </Typography>
      </Button>

      <Menu
        anchorEl={openMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        disableScrollLock={true}
        open={openMenuViewFilter}
        onClose={handleMenuClose}
      >
        <Typography
          sx={{
            fontWeight: "500",
            fontSize: "0.8rem",
            paddingLeft: 2,
            paddingTop: 1,
            paddingBottom: 1,
          }}
        >
          {t("moodboards.settings.chooseRange")}
        </Typography>

        {options.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleMenuItemClick(option)}
            selected={selected === option.id}
          >
            <List dense={true} sx={{ margin: 0, padding: 0 }}>
              <ListItem sx={{ margin: 0, padding: 0 }}>
                <ListItemIcon sx={{ width: "40px" }}>
                  {option.icon}
                </ListItemIcon>
                <ListItemText primary={option.title} />
              </ListItem>
            </List>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

export default TimeViewSelector;
